import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Heart } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

export default function AuthPage() {
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState(searchParams.get("mode") === "register" ? "register" : "login");
  const [form, setForm] = useState({ email: "", password: "", name: "", invite_code: "" });
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const url = mode === "login" ? "/auth/login" : "/auth/register";
      const payload = mode === "login"
        ? { email: form.email, password: form.password }
        : form;
      const { data } = await api.post(url, payload);
      login(data.token, data.user);
      toast.success(mode === "login" ? "Welcome back" : "Account created");
      if (data.user.role === "admin") navigate("/admin");
      else if (data.user.onboarding_complete) navigate("/radar");
      else navigate("/onboarding");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-stretch" data-testid="auth-page">
      <div className="hidden lg:block lg:w-1/2 relative">
        <div className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url(https://customer-assets.emergentagent.com/job_radar-romance-1/artifacts/pqmjtz1q_Love%20coordinates.png)" }} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-amber-200/20" />
        <div className="relative z-10 p-12 flex flex-col justify-end h-full">
          <h2 className="font-display font-bold text-4xl leading-tight text-white drop-shadow">Every connection<br />has coordinates.</h2>
          <p className="text-white/80 mt-3 drop-shadow">New York City · Membership by invitation only.</p>
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12 sunny-bg">
        <div className="w-full max-w-md fade-up">
          <div className="flex items-center gap-2 mb-10">
            <Heart className="text-[#F43F5E] fill-[#F43F5E]" size={24} />
            <span className="font-display font-bold text-lg text-stone-900">Love at geo-point</span>
          </div>

          <h1 className="font-display font-extrabold text-3xl tracking-tight text-stone-900">
            {mode === "login" ? "Sign in" : "Create your profile"}
          </h1>
          <p className="text-stone-500 mt-2 mb-8">
            {mode === "login" ? "Continue your search." : "You'll need a valid invite code to join."}
          </p>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <Label className="text-stone-600">Full name</Label>
                <Input data-testid="name-input" value={form.name} onChange={set("name")} required
                  className="mt-1.5 bg-white border-stone-200 focus:border-[#F43F5E]" placeholder="Jane Doe" />
              </div>
            )}
            <div>
              <Label className="text-stone-600">Email</Label>
              <Input data-testid="email-input" type="email" value={form.email} onChange={set("email")} required
                className="mt-1.5 bg-white border-stone-200 focus:border-[#F43F5E]" placeholder="you@email.com" />
            </div>
            <div>
              <Label className="text-stone-600">Password</Label>
              <Input data-testid="password-input" type="password" value={form.password} onChange={set("password")} required
                className="mt-1.5 bg-white border-stone-200 focus:border-[#F43F5E]" placeholder="••••••••" />
            </div>
            {mode === "register" && (
              <div>
                <Label className="text-stone-600">Invite code</Label>
                <Input data-testid="invite-input" value={form.invite_code} onChange={set("invite_code")} required
                  className="mt-1.5 bg-white border-stone-200 focus:border-[#F43F5E] uppercase tracking-widest" placeholder="XXXXXXXX" />
              </div>
            )}
            <Button data-testid="auth-submit-btn" type="submit" disabled={loading}
              className="w-full bg-[#F43F5E] hover:bg-[#E11D48] text-white rounded-full h-11 font-semibold shadow-lg shadow-rose-500/25">
              {loading ? "Please wait…" : mode === "login" ? "Sign in" : "Join the club"}
            </Button>
          </form>

          <button data-testid="toggle-mode-btn" onClick={() => setMode(mode === "login" ? "register" : "login")}
            className="text-sm text-stone-500 hover:text-stone-900 mt-6 transition-colors">
            {mode === "login" ? "Have an invite code? Create profile" : "Already a member? Sign in"}
          </button>
        </div>
      </div>
    </div>
  );
}
